const SWR_CACHE_PREFIX = 'staleWhileRevalidate:'

type CachedEntry = {
  cachedAt: number
}

type EvictOptions = {
  /** Maximum number of entries to evict. Defaults to half of all SWR entries. */
  maxEntriesToEvict?: number
}

/**
 * Scans localStorage for SWR cache entries (`staleWhileRevalidate:` prefix),
 * sorts by `cachedAt` oldest-first, and removes up to half (or `maxEntriesToEvict`).
 * Returns the count of entries evicted. Never throws.
 */
export const evictSwrCacheEntries = (options?: EvictOptions): number => {
  try {
    const swrEntries: { key: string; cachedAt: number }[] = []

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (!key || !key.startsWith(SWR_CACHE_PREFIX)) continue

      let cachedAt = 0
      try {
        const parsed = JSON.parse(localStorage.getItem(key)!) as CachedEntry
        cachedAt = parsed?.cachedAt ?? 0
      } catch {
        // Unparseable entry — treat as oldest (cachedAt = 0) so it gets evicted first
      }

      swrEntries.push({ key, cachedAt })
    }

    if (swrEntries.length === 0) return 0

    // Sort oldest first
    swrEntries.sort((a, b) => a.cachedAt - b.cachedAt)

    const limit = options?.maxEntriesToEvict ?? Math.ceil(swrEntries.length / 2)
    const toEvict = swrEntries.slice(0, limit)

    for (const entry of toEvict) {
      localStorage.removeItem(entry.key)
    }

    return toEvict.length
  } catch {
    return 0
  }
}
